/**
 * MedicalConsultationListPage Component
 * Main page for medical consultation queue management
 *
 * @version 2025.1.0
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Chip,
  Card,
  CardContent,
  Grid,
  Skeleton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  Business as CompanyIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  PictureAsPdf as PdfIcon,
  Description as ProntuarioIcon,
  Campaign as CallIcon,
} from '@mui/icons-material';
import { useMedicalConsultation } from '../../hooks';
import { ConsultationQueueItem } from '../../types/medicalRecord';
import { Company } from '../../types/company';
import companyService from '../../services/companyService';
import medicalRecordService from '../../services/medicalRecordService';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  InlineFilterBar,
} from '../../components';
import type { ActiveChip } from '../../hooks/useFilterState';
import { openSignedFile } from '../../services/fileAccess';


// Format wait time
const formatWaitTime = (presenceConfirmedAt: string | null): string => {
  if (!presenceConfirmedAt) return '-';
  try {
    return formatDistanceToNow(parseISO(presenceConfirmedAt), {
      addSuffix: false,
      locale: ptBR,
    });
  } catch {
    return '-';
  }
};

// Get attendance status color
const getStatusColor = (status: string): 'default' | 'info' | 'warning' | 'success' | 'error' | 'secondary' => {
  const colors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error' | 'secondary'> = {
    NOT_ATTENDED: 'default',
    CHECKED_IN: 'info',
    TRIAGED: 'secondary',
    IN_PROGRESS: 'warning',
    COMPLETED: 'success',
    NO_SHOW: 'error',
  };
  return colors[status] || 'default';
};

// Get attendance status label
const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    NOT_ATTENDED: 'Não chegou',
    CHECKED_IN: 'Aguardando',
    TRIAGED: 'Triagem Feita',
    IN_PROGRESS: 'Em atendimento',
    COMPLETED: 'Concluído',
    NO_SHOW: 'Falta',
  };
  return labels[status] || status;
};

const columns: DataTableColumn<ConsultationQueueItem>[] = [
  {
    id: 'queueNumber',
    label: 'Senha',
    render: (item) => (
      <Chip label={item.queueNumber ?? '-'} size="small" color="primary" variant="outlined" />
    ),
  },
  {
    id: 'patient',
    label: 'Paciente',
    primary: true,
    render: (item) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {item.employee?.name || 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          CPF: {item.employee?.cpf || 'N/A'}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (item) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CompanyIcon fontSize="small" color="action" />
        <Typography variant="body2">
          {item.employee?.company?.tradeName || item.employee?.company?.legalName || 'N/A'}
        </Typography>
      </Box>
    ),
  },
  { id: 'examType', label: 'Tipo de Exame', render: (item) => item.examType },
  {
    id: 'hour',
    label: 'Horário',
    render: (item) => (item.hour && item.minute ? `${item.hour}:${item.minute}` : '-'),
  },
  {
    id: 'waitTime',
    label: 'Tempo Espera',
    render: (item) => formatWaitTime(item.presenceConfirmedAt),
  },
  {
    id: 'status',
    label: 'Status',
    render: (item) => (
      <Chip
        label={getStatusLabel(item.attendanceStatus)}
        size="small"
        color={getStatusColor(item.attendanceStatus)}
      />
    ),
  },
];

export const MedicalConsultationListPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<'all' | 'CHECKED_IN' | 'IN_PROGRESS' | 'COMPLETED'>('CHECKED_IN');

  // Hooks
  const {
    queue,
    loading,
    filters,
    socketConnected,
    updateFilter,
    clearFilters,
    refresh,
    generateProntuario,
  } = useMedicalConsultation({
    initialFilters: {
      date: new Date().toISOString().split('T')[0],
      // Load all statuses to have accurate counts (TRIAGED = ready for consultation after triage)
      attendanceStatus: ['CHECKED_IN', 'TRIAGED', 'IN_PROGRESS', 'COMPLETED'],
    },
    autoLoad: true,
  });

  // Calculate queue statistics from loaded data
  // TRIAGED and CHECKED_IN both count as "waiting" (ready for consultation)
  const queueStats = {
    waiting: queue.filter((q) => q.attendanceStatus === 'CHECKED_IN' || q.attendanceStatus === 'TRIAGED').length,
    inProgress: queue.filter((q) => q.attendanceStatus === 'IN_PROGRESS').length,
    completed: queue.filter((q) => q.attendanceStatus === 'COMPLETED').length,
    total: queue.length,
  };

  // Filter displayed queue based on selected status
  // When filtering by CHECKED_IN (Aguardando), also include TRIAGED status
  const displayedQueue = activeStatusFilter === 'all'
    ? queue
    : activeStatusFilter === 'CHECKED_IN'
      ? queue.filter((q) => q.attendanceStatus === 'CHECKED_IN' || q.attendanceStatus === 'TRIAGED')
      : queue.filter((q) => q.attendanceStatus === activeStatusFilter);

  // Handle status card click - filter display (not reload)
  const handleStatusCardClick = useCallback((status: 'all' | 'CHECKED_IN' | 'IN_PROGRESS' | 'COMPLETED') => {
    setActiveStatusFilter(status);
  }, []);

  // Handle view existing ASO
  const handleViewAso = useCallback(async (pdfPath: string) => {
    const target = window.open('about:blank', '_blank');
    if (!target) return;
    try {
      await openSignedFile(pdfPath, target);
    } catch (err) {
      target.close();
      console.error('Erro ao abrir ASO:', err);
    }
  }, []);

  // Handle Prontuário generation with auto-open
  const handleGenerateProntuario = useCallback(
    async (id: number) => {
      const target = window.open('about:blank', '_blank');
      const result = await generateProntuario(id);
      if (result?.pdfPath) {
        if (target) {
          try {
            await openSignedFile(result.pdfPath, target);
          } catch (err) {
            target.close();
            console.error('Erro ao abrir Prontuário:', err);
          }
        }
      } else if (target) {
        target.close();
      }
      return result;
    },
    [generateProntuario]
  );

  // Date change handler
  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        updateFilter('date', format(date, 'yyyy-MM-dd'));
      }
    },
    [updateFilter]
  );

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await companyService.getCompanies({ limit: 100 });
        setCompanies(response.data ?? []);
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    loadCompanies();
  }, []);

  // Company filter change (shared by the Autocomplete and the "Empresa" chip's onDelete)
  const applyCompanyFilter = useCallback(
    (value: Company | null) => {
      setSelectedCompany(value);
      updateFilter('companyId', value?.id ? Number(value.id) : undefined);
    },
    [updateFilter]
  );

  const handleCompanyChange = useCallback(
    (_: React.SyntheticEvent, value: Company | null) => applyCompanyFilter(value),
    [applyCompanyFilter]
  );

  // Search filter change (InlineFilterBar already debounces internally)
  const handleSearchChange = useCallback(
    (value: string) => {
      updateFilter('search', value || undefined);
    },
    [updateFilter]
  );

  // Clear all filters (inline bar's "Limpar tudo") — also resets the company
  // Autocomplete's local selection and the status card back to its default.
  const handleClearAll = useCallback(() => {
    clearFilters();
    setSelectedCompany(null);
    setActiveStatusFilter('CHECKED_IN');
  }, [clearFilters]);

  // Call patient (update queueCalledAt for TV display)
  const handleCallPatient = useCallback(
    async (appointmentId: number) => {
      try {
        await medicalRecordService.callPatient(appointmentId);
        refresh();
      } catch (error) {
        console.error('Error calling patient:', error);
      }
    },
    [refresh]
  );

  // Start/continue consultation — navigate to dedicated page
  const handleStartConsultation = useCallback(
    (item: ConsultationQueueItem) => {
      navigate(`/medical-consultations/${item.id}`);
    },
    [navigate]
  );

  // Active filter chips (only shown when the value differs from the screen's default)
  const todayIso = new Date().toISOString().split('T')[0];
  const filterChips: ActiveChip[] = [];
  if (selectedCompany) {
    filterChips.push({
      key: 'companyId',
      label: 'Empresa',
      value: selectedCompany.tradeName || selectedCompany.legalName || '',
      onDelete: () => applyCompanyFilter(null),
    });
  }
  if (filters.date && filters.date !== todayIso) {
    filterChips.push({
      key: 'date',
      label: 'Data',
      value: format(parseISO(filters.date), 'dd/MM/yyyy'),
      onDelete: () => handleDateChange(new Date()),
    });
  }

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Consultas Médicas' },
      ]}
      title="Consultas Médicas"
      actions={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={socketConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: socketConnected ? 'success.main' : 'warning.main',
              }}
            />
          </Tooltip>
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={refresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      }
    >
      {/* Summary Cards - Clickable Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card
            onClick={() => handleStatusCardClick('CHECKED_IN')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: activeStatusFilter === 'CHECKED_IN' ? 2 : 1,
              borderColor: activeStatusFilter === 'CHECKED_IN' ? 'info.main' : 'divider',
              bgcolor: activeStatusFilter === 'CHECKED_IN' ? 'info.50' : 'background.paper',
              '&:hover': {
                boxShadow: 3,
                borderColor: 'info.main',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Aguardando
                </Typography>
              </Box>
              {loading ? (
                <Skeleton width={60} height={40} />
              ) : (
                <Typography variant="h4">
                  {queueStats.waiting}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card
            onClick={() => handleStatusCardClick('IN_PROGRESS')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: activeStatusFilter === 'IN_PROGRESS' ? 2 : 1,
              borderColor: activeStatusFilter === 'IN_PROGRESS' ? 'warning.main' : 'divider',
              bgcolor: activeStatusFilter === 'IN_PROGRESS' ? 'warning.50' : 'background.paper',
              '&:hover': {
                boxShadow: 3,
                borderColor: 'warning.main',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DoctorIcon color="warning" />
                <Typography variant="subtitle2" color="text.secondary">
                  Em Atendimento
                </Typography>
              </Box>
              {loading ? (
                <Skeleton width={60} height={40} />
              ) : (
                <Typography variant="h4">
                  {queueStats.inProgress}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card
            onClick={() => handleStatusCardClick('COMPLETED')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: activeStatusFilter === 'COMPLETED' ? 2 : 1,
              borderColor: activeStatusFilter === 'COMPLETED' ? 'success.main' : 'divider',
              bgcolor: activeStatusFilter === 'COMPLETED' ? 'success.50' : 'background.paper',
              '&:hover': {
                boxShadow: 3,
                borderColor: 'success.main',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalIcon color="success" />
                <Typography variant="subtitle2" color="text.secondary">
                  Concluídos Hoje
                </Typography>
              </Box>
              {loading ? (
                <Skeleton width={60} height={40} />
              ) : (
                <Typography variant="h4">
                  {queueStats.completed}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card
            onClick={() => handleStatusCardClick('all')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: activeStatusFilter === 'all' ? 2 : 1,
              borderColor: activeStatusFilter === 'all' ? 'primary.main' : 'divider',
              bgcolor: activeStatusFilter === 'all' ? 'primary.50' : 'background.paper',
              '&:hover': {
                boxShadow: 3,
                borderColor: 'primary.main',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total na Fila
                </Typography>
              </Box>
              {loading ? (
                <Skeleton width={60} height={40} />
              ) : (
                <Typography variant="h4">{queueStats.total}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <InlineFilterBar
            searchPlaceholder="Buscar paciente..."
            searchValue={filters.search ?? ''}
            onSearchChange={handleSearchChange}
            resultCount={displayedQueue.length}
            resultNoun="consultas"
            chips={filterChips}
            onClearAll={handleClearAll}
            loading={loading}
          >
            <DatePicker
              label="Data"
              value={filters.date ? parseISO(filters.date) : null}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 170 },
                },
              }}
            />
            <Autocomplete
              size="small"
              options={companies}
              getOptionLabel={(option) => (option.tradeName || option.legalName) ?? ''}
              value={selectedCompany}
              onChange={handleCompanyChange}
              sx={{ minWidth: 190 }}
              renderInput={(params) => <TextField {...params} label="Empresa" />}
            />
          </InlineFilterBar>
        </LocalizationProvider>
      </Box>

      <DataTable
        columns={columns}
        rows={displayedQueue}
        getRowId={(item) => item.id}
        loading={loading}
        emptyState={
          activeStatusFilter === 'all'
            ? {
                title: 'Nenhum paciente na fila de consultas',
                description:
                  'A fila é formada pelos pacientes que fizeram check-in na recepção e aguardam atendimento médico.',
              }
            : {
                title: `Nenhum paciente com status "${getStatusLabel(activeStatusFilter)}"`,
                description:
                  'A fila é formada pelos pacientes que fizeram check-in na recepção; outros status podem ter pacientes agora.',
              }
        }
        actions={[
          {
            icon: <CallIcon />,
            tooltip: 'Chamar Paciente',
            color: 'secondary',
            onClick: (item) => handleCallPatient(item.id),
            hidden: (item) =>
              !(
                (item.attendanceStatus === 'CHECKED_IN' || item.attendanceStatus === 'TRIAGED') &&
                Boolean(item.queueNumber)
              ),
          },
          {
            icon: <StartIcon />,
            tooltip: 'Iniciar Atendimento',
            color: 'primary',
            onClick: handleStartConsultation,
            hidden: (item) =>
              item.attendanceStatus !== 'CHECKED_IN' && item.attendanceStatus !== 'TRIAGED',
          },
          {
            icon: <ViewIcon />,
            tooltip: 'Continuar Atendimento',
            color: 'warning',
            onClick: handleStartConsultation,
            hidden: (item) => item.attendanceStatus !== 'IN_PROGRESS',
          },
          {
            icon: <ViewIcon />,
            tooltip: 'Ver Consulta',
            color: 'success',
            onClick: handleStartConsultation,
            hidden: (item) => item.attendanceStatus !== 'COMPLETED' || !item.medicalRecord,
          },
          {
            icon: <ProntuarioIcon />,
            tooltip: 'Gerar Prontuário PDF',
            color: 'info',
            onClick: (item) => handleGenerateProntuario(item.medicalRecord!.id),
            hidden: (item) => item.attendanceStatus !== 'COMPLETED' || !item.medicalRecord,
          },
          {
            icon: <PdfIcon />,
            tooltip: 'Baixar ASO',
            color: 'secondary',
            onClick: (item) => handleViewAso(item.medicalRecord!.asoPdfPath!),
            hidden: (item) =>
              item.attendanceStatus !== 'COMPLETED' ||
              !item.medicalRecord ||
              !item.medicalRecord.asoPdfPath,
          },
        ]}
      />
    </PageShell>
  );
};

export default MedicalConsultationListPage;
